import {Injectable} from '@angular/core';
import {forkJoin, Observable, of, timer} from 'rxjs';
import {catchError, map, shareReplay, switchMap} from 'rxjs/operators';
import {DevicesService} from './devices.service';
import {TilesService} from './tiles.service';
import {PermissionsService} from './permissions.service';
import {SessionService} from './session.service';
import {Permission} from '../../shared/models/fga/permission';
import {ObjectType} from '../../shared/models/object.model';
import {Tile} from '../../shared/models/tile';
import {SimpleAnalogStructure} from '../../shared/models/structure';
import {FormulaBinding} from '../../shared/models/formula-config';
import {
  FormulaInputDetail,
  FormulaInputReading,
  FormulaInputStaleness,
} from '../../shared/models/formula-staleness';
import {
  DATA_AGE_REFRESH_INTERVAL_MS,
  DEFAULT_STALE_THRESHOLD_MINUTES,
  FORMULA_STALENESS_REFRESH_INTERVAL_MS,
} from '../../shared/constants/tile-config.constants';
import {Device, DeviceId, DeviceType} from '../../shared/models/device';
import {getDataAgeStatus} from '../../shared/utils/data-age.util';
import {IconGuesserUtil} from '../../shared/utils/icon-guesser.util';

const NO_DATA_LABEL = 'No data';

@Injectable({
  providedIn: 'root',
})
export class FormulaStalenessService {
  private sourceDevices = new Map<DeviceId, Observable<Device | null>>();

  constructor(
    private devicesService: DevicesService,
    private tilesService: TilesService,
    private permissionsService: PermissionsService,
    private session: SessionService,
  ) {
  }

  getInputStaleness$(tile: Tile): Observable<FormulaInputStaleness | null> {
    return this.fetchReadings$(tile).pipe(
      map(readings => readings && this.toStalenessFromReadings(readings)),
    );
  }

  watchInputStaleness$(tile: Tile): Observable<FormulaInputStaleness | null> {
    return timer(0, FORMULA_STALENESS_REFRESH_INTERVAL_MS).pipe(
      switchMap(() => this.fetchReadings$(tile)),
      switchMap(readings => readings
        ? timer(0, DATA_AGE_REFRESH_INTERVAL_MS).pipe(
          map(() => this.toStalenessFromReadings(readings)),
        )
        : of(null),
      ),
    );
  }

  private fetchReadings$(tile: Tile): Observable<FormulaInputReading[] | null> {
    if (!tile.isFormulaTile()) {
      return of(null);
    }

    const structure = tile.Structure as SimpleAnalogStructure;

    return this.tilesService.canConfigureTile$(tile.ID).pipe(
      switchMap(allowed => allowed ? this.devicesService.fetchDevice$(structure.DeviceID) : of(null)),
      switchMap(device => {
        const bindings: FormulaBinding[] = device?.Config?.Formula?.Points
          ?.find(point => point.ID === structure.PointID)
          ?.Bindings ?? [];

        if (bindings.length === 0) {
          return of(null);
        }

        return this.canConfigureSources$(bindings).pipe(
          switchMap(allowed => allowed
            ? forkJoin(bindings.map(binding => this.getBindingReading$(binding)))
            : of(null),
          ),
        );
      }),
      catchError(() => of(null)),
    );
  }

  private canConfigureSources$(bindings: FormulaBinding[]): Observable<boolean> {
    const deviceIds = [...new Set(bindings.map(binding => binding.Source.DeviceID))];

    return forkJoin(deviceIds.map(deviceId => this.permissionsService.hasPermissions({
      UserID: this.session.userId,
      Permissions: [Permission.CAN_CONFIGURE],
      ObjectID: deviceId,
      ObjectType: ObjectType.DEVICE,
    }))).pipe(map(results => results.every(Boolean)));
  }

  private toStalenessFromReadings(readings: FormulaInputReading[]): FormulaInputStaleness {
    const inputs = readings.map(r => this.toDetail(r.variable, r.time, r.pointName));
    const staleInputs = inputs.filter(i => i.isStale);
    const freshInputs = inputs.filter(i => !i.isStale);
    const longest = staleInputs.reduce((oldest, i) => i.ageSeconds > oldest.ageSeconds ? i : oldest, staleInputs[0]);

    return {
      total: inputs.length,
      stalePercent: 100 * staleInputs.length / inputs.length,
      staleInputs,
      freshInputs,
      sections: [
        {label: 'Stale', stale: true, inputs: staleInputs},
        {label: 'Not stale', stale: false, inputs: freshInputs},
      ].filter(section => section.inputs.length > 0),
      longestStaleAgeText: longest?.ageText ?? '',
    };
  }

  private getBindingReading$(binding: FormulaBinding): Observable<FormulaInputReading> {
    const {DeviceID, PointID} = binding.Source;

    return forkJoin({
      values: this.devicesService.readPointValue$(PointID, DeviceID).pipe(catchError(() => of([]))),
      device: this.getSourceDevice$(DeviceID),
    }).pipe(
      map(({values, device}) => ({
        variable: binding.Variable,
        pointName: device ? this.resolvePointName(device, PointID) : undefined,
        time: values?.[0]?.Time,
      })),
    );
  }

  private getSourceDevice$(deviceId: DeviceId): Observable<Device | null> {
    if (!this.sourceDevices.has(deviceId)) {
      this.sourceDevices.set(deviceId, this.devicesService.fetchDevice$(deviceId).pipe(
        catchError(() => {
          this.sourceDevices.delete(deviceId);
          return of(null);
        }),
        shareReplay(1),
      ));
    }

    return this.sourceDevices.get(deviceId)!;
  }

  private resolvePointName(device: Device, pointId: string): string | undefined {
    switch (device.Type) {
      case DeviceType.LoRaWAN:
        try {
          return (JSON.parse(pointId) as string[])[1] || undefined;
        } catch {
          return undefined;
        }
      case DeviceType.Modbus:
        return device.Config?.Modbus?.Points?.find(p => p.ID === pointId)?.Name || undefined;
      default:
        return undefined;
    }
  }

  private toDetail(variable: string, time: string | undefined, pointName?: string): FormulaInputDetail {
    const icon = `fa-regular fa-${IconGuesserUtil.guessIconFromText(pointName ?? variable)}`;

    if (!time) {
      return {variable, pointName, icon, isStale: true, ageText: NO_DATA_LABEL, ageSeconds: Number.MAX_SAFE_INTEGER};
    }

    const age = getDataAgeStatus(time, {minAgeMinutes: DEFAULT_STALE_THRESHOLD_MINUTES});

    return {variable, pointName, icon, isStale: age.isStale, ageText: age.label, ageSeconds: age.ageSeconds};
  }
}
